import { getRecoil, setRecoil } from "recoil-nexus";

import selectionsStore, { storeChangeSelection } from "src/stores/selections";
import betBuilderStore from "src/stores/betBuilder";
import { ISelection } from "src/constants/interfaces/bet";
import { toastWrapper } from "src/common/components/toastWrapper";
import {
  getBetBuilderOdds,
  IBetBuilderOddsSelection,
} from "./betBuilder";

// Mirrors the score-parsing fallback the validation hook uses — kept local so
// this service has no React/hook dependencies.
const parseScoreFromName = (
  name?: string,
): { home: number; away: number } | null => {
  if (!name) return null;
  const m = name.trim().match(/^(\d+)\s*[:\-]\s*(\d+)$/);
  return m ? { home: Number(m[1]), away: Number(m[2]) } : null;
};

const toOddsPayload = (s: ISelection): IBetBuilderOddsSelection => {
  const fb = parseScoreFromName(s.event.name);
  // Same SelectionName fallback as `toOddsPayload` in betBuilderValidation:
  // type → type_1 → name. `Kind` is a v2-markets-response-only field, not
  // present on swarm events, so it can't be used here.
  const ev = s.event as typeof s.event & { type_1?: string };
  const selectionName = ev.type || ev.type_1 || ev.name || '';
  return {
    selectionId: s.event.id,
    homeValue: s.event.home_value ?? fb?.home ?? 0,
    awayValue: s.event.away_value ?? fb?.away ?? 0,
    selectionTypeId: s.event.type_id,
    marketId: s.market.id,
    selectionName,
    price: s.event.price,
  };
};

/**
 * Imperative, non-hook refresh of `get_bet_builder_odds`. Used after a
 * `do_bet_terminal` rejection (typically "odds have been changed") on a slip
 * that contains bet-builder legs: we re-validate the BB combinations so the
 * displayed combined odd matches what the server would now accept.
 *
 * Differs from `useBetBuilderValidation` in ONE place: server-returned
 * `RejectedIds` are marked `inactive: true` on each selection INSTEAD of
 * being removed from the slip. That way the user sees the leg flagged as
 * deleted inside the BB card (red + block icon), the standard
 * `hasInactiveSelections` validator surfaces the "There are deleted events"
 * notify message, and place-bet stays disabled until the user removes the
 * leg manually.
 *
 * Already-inactive selections are excluded from the request so a previously
 * deleted leg can't poison the new combined odd.
 */
export const refreshBetBuilderOddsKeepingRejected = async (): Promise<void> => {
  const { selections } = getRecoil(selectionsStore);

  // Only consider live BB legs — skip anything already flagged inactive so
  // the post-rejection refresh prices the still-valid combination.
  const bbSelections = selections.filter(
    (s) => s.event.isInternalBetBuilder && !s.inactive,
  );
  if (!bbSelections.length) return;

  const gameIdsInPlay = new Set(bbSelections.map((s) => s.game.id as number));

  // Flip groups to loading so the slip UI shows the in-flight state while
  // the new odds are fetched.
  setRecoil(betBuilderStore, (prev) => ({
    ...prev,
    groups: Object.fromEntries(
      Array.from(gameIdsInPlay).map((gameId) => [
        gameId,
        {
          combinedPrice: prev.groups[gameId]?.combinedPrice ?? 0,
          selectionIds: bbSelections
            .filter((s) => (s.game.id as number) === gameId)
            .map((s) => s.event.id),
          loading: true,
        },
      ]),
    ),
  }));

  try {
    const details = await getBetBuilderOdds(bbSelections.map(toOddsPayload));

    // Apply rejected ids as inactive — preserves them in the slip and lets
    // the existing inactive-handling rules (notify message + place-bet
    // block) take over.
    if (details.RejectedIds.length) {
      details.RejectedIds.forEach((id) => {
        storeChangeSelection(id, (old) => ({ ...old, inactive: true }));
      });
      toastWrapper(
        "error",
        details.RejectedIds.length === 1
          ? "A bet builder selection is no longer available"
          : "Some bet builder selections are no longer available",
      );
    }

    const perGame: Record<number, number> = Object.fromEntries(
      details.OddsPerMatch.map((item) => [item.MatchId, item.Odd]),
    );

    setRecoil(betBuilderStore, {
      totalOdd: details.TotalOdd,
      rejectedIds: details.RejectedIds,
      groups: Object.fromEntries(
        Array.from(gameIdsInPlay).map((gameId) => [
          gameId,
          {
            combinedPrice: perGame[gameId] ?? 0,
            selectionIds: bbSelections
              .filter(
                (s) =>
                  (s.game.id as number) === gameId
                  && !details.RejectedIds.includes(s.event.id),
              )
              .map((s) => s.event.id),
            loading: false,
          },
        ]),
      ),
    });
  } catch {
    toastWrapper("error", "Bet builder service unavailable");
    setRecoil(betBuilderStore, (prev) => ({
      ...prev,
      groups: Object.fromEntries(
        Object.entries(prev.groups).map(([gameId, group]) => [
          gameId,
          { ...group, loading: false },
        ]),
      ),
    }));
  }
};
